import {
	Autocomplete,
	Box,
	Breadcrumbs,
	Button,
	Container,
	Divider,
	Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    ListItem,
	Grid,
	IconButton,
	Link,
	MenuItem,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import queryGraphql from "../../helpers/ApiInterface";
import { useAuth } from "../../helpers/AuthProvider";
import MiniBot from "../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed
interface ApplicationRequesterFormProps {
	isEdit: boolean;
}
interface ApplicationRequesterFormContentProps {
	isEdit: boolean;
}
interface AppData{
	applicationName: string;
	environment: string;
}
interface OneApplication {
	id: string;
	applicationName: string;
	status: string;
	environment: string;
  }
interface OneCluster {
	id: string;
	clusterName: string;
	environment: string;
	status: string; // Or a more specific type if you know the possible values (e.g., enum)
	applications: OneApplication[];
  }
  
const validationSchema = Yup.object().shape({
	applicationName: Yup.string()
		.required("This field is required")
		.strict()
		.lowercase("This field must be in lowercase")
		.matches(/^[a-zA-Z0-9\s]*$/, "This field must be alphanumeric(lowercase and numbers only)")
		.max(20, "This field must not exceed 20 characters"),
	gcpApplicationViewers: Yup.array()
		.of(
			Yup.string()
				.required("This field is required")
				.matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain")
				.strict()
		)
		.required("This field is required")
		.min(1, "This field is required")
		.strict(),
	cluster: Yup.string().required("This field is required").strict(),

	serviceRequesters: Yup.array()
		.of(
			Yup.string()
				.required("This field is required")
				.matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain")
				.strict()
		)
		.required("This field is required")
		.min(1, "This field is required")
		.strict(),
	costLabelPairs: Yup.array()
		.of(
			Yup.object({
				cost: Yup.string()
					.required("This field is required")
					.strict()
					.lowercase("This field must be in lowercase")
					.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
					.max(20, "This field must not exceed 20 characters"),
				label: Yup.string()
					.required("This field is required")
					.strict()
					.lowercase("This field must be in lowercase")
					.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
					.max(20, "This field must not exceed 20 characters"),
			})
		)
		.required("This field is required"),
	environment: Yup.string().required("This field is required").oneOf(["dev", "staging", "prod"]),
	costCenterValue: Yup.string()
		.required("This field is required")
		.strict()
		.lowercase("This field must be in lowercase")
		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
		.max(20, "This field must not exceed 20 characters"),
});
const ApplicationRequesterFormContent: React.FC<ApplicationRequesterFormContentProps> = ({ isEdit }) => {
	const navigate = useNavigate();
	const [application, setApplication] = useState<any>({});
	
	const [currentCluster,setCurrentCluster] = useState<any>([]);
	const [devEnvChecker,setDevEnvChecker] = useState<boolean>(false);
	const [dummyEnvs, setDummyEnvs] = useState<any>([]);
	const [currentApplication, setCurrentApplication] = useState<any>();
	const [allApps,setAllApps] = useState<AppData[]>([]);
	const [clusters, setClusters] = useState([]);
	const [clustersNamesList, setClustersNamesList] = useState<any>([]);
	const [users, setUsers] = useState([]);
	const { id } = useParams();

	const [applications,setApplications] = useState<any>([]);
	const [envs, setEnvs] = useState<any>([]);
	
	const { user } = useAuth();
	const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
	const addAllApps = (data1: string, data2: string) =>{
		if(data1== currentApplication && data2== 'dev')
			setDevEnvChecker(true);
		setAllApps((prevItems) => [...prevItems,{applicationName: data1, environment: data2}])
	}
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleOpenModal = () => setIsModalOpen(true);
	const handleCloseModal = () => setIsModalOpen(false);
	React.useEffect(() => {
        if (currentCluster) { 
            setDummyEnvs(currentCluster);
            // console.log("dummyEnvs updated", dummyEnvs);
        }
    }, [currentCluster,envs]);

	// This chunk of code input dummy values in dummy varaible and this is used to re render the clusters data useffect to render again and again every second to manage all the asynchronous calls and efficiently help the user input correct data in the application requester form 
	React.useEffect(() => {
		const intervalId = setInterval(() => {
		  setDummyEnvs(new Date().toLocaleTimeString());
		}, 2000);
	
		return () => {
		  clearInterval(intervalId);
		};
	  }, []);
	React.useEffect(() => {
		(async () => {
			try {
				const data = await queryGraphql(`
                        query{
                        users {
						id,
                        email
                        }
                        }
					`);
				if (data.users) {
					setUsers(data.users);
				}
			} catch (error) {
				console.log(error);
			}
		})();
	}, []);
	React.useEffect(() => {
		(async () => {
			try {
				const data = await queryGraphql(
					`
                       query{
                        clusters {
						id,
                        clusterName,
                        environment,
						status,
						applications {
							id
							applicationName
							status
							environment
							}
                        }
                        }
					`
				);

				const data2 = await queryGraphql(`
					query Users {
							users {
								id,
								name,
								email,
								clustersViewable {
								clusterName,
								id,
								environment,
								}
								applicationsViewable {
								applicationName,
								id,
								environment,
								}
							}
							}
				`);
			// if(data2.users)	{
			// 	console.log("clsuter viewable", data2.users.filter((e:any) => e.email == user.email).map((e:any)=> e.clustersViewable).flat().map((e:any)=> e.id) );
			// }
				// console.log(data.clusters);
				if (data.clusters) {
					setClusters(data.clusters);
					setClustersNamesList(
						Array.from(
							new Set(
								(data.clusters
									?.filter((e: any) => e.status == "active") 
									.filter((e: any) => { 
										if (isViewer) {
											return true; 
										}
										return data2.users
											?.filter((eUser: any) => eUser.email == user.email)
											.map((eUserMap: any) => eUserMap.clustersViewable)
											.flat()
											.map((eViewableCluster: any) => eViewableCluster.id)
											.includes(e.id);
									})
									.map((e: any) => e.clusterName) 
								) || [] 
							)
						)
					);
					
				
				// setEnvs([]);
				setApplications([]);
				setAllApps([]);
				setDevEnvChecker(false);
				data.clusters.map((p: OneCluster) => {
					// console.log("p.clusterName->",p.clusterName);
					// console.log('currentCluster->', currentCluster);
					if(p.clusterName === currentCluster){
						
						p.applications.map((e: OneApplication)=>((e.status == "active" || e.status == "pending") ? addAllApps(e.applicationName,e.environment): null));
						// console.log("envs->",envs);
						
					}
					// console.log(p.clusterName);
					// console.log("Applications",applications);
					// console.log(allApps);
				}
			);
			// console.log("applications->",applications);
			// console.log("platfotm->", currentCluster)
			// console.log("envs->",envs);
				}
			} catch (error) {
				console.log(error);
			}
		})();
	}, [currentApplication,currentCluster,dummyEnvs]);
	React.useEffect(() => {
		(async () => {
			try {
				const data = await queryGraphql(
					`
                        query($applicationId: String!){
                        application(id:$applicationId) {
						id,
                        applicationName,
						gcpApplicationViewers,
						cluster{
						id,
						clusterName,
						environment
						},
						serviceRequesters,
						costLabelPairs{
						cost,
						label
						},
						environment,
		
                        }
                        }
`,
					{ applicationId: id }
				);
				if (data.application) {
					setApplication(data.application);
				}
			} catch (error) {
				console.log(error);
			}
		})();
	}, []);

	return (
		<Container
			disableGutters
			maxWidth={false}
			sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
		>
			<MiniBot/>
			<Box sx={{ mb: 4 }}>
				<Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
					<Link underline="hover" color="inherit" href="/applications">
						Applications
					</Link>
					<Typography color="text.primary">
						{isEdit ? "Edit Application Details" : "Add New K8s Application"}
					</Typography>
				</Breadcrumbs>
				<Box display="flex" alignItems="center" mt={2}>
					<IconButton
						edge="start"
						aria-label="back"
						color="inherit"
						onClick={() => navigate("/applications")}
					>
						<ArrowBackIosNewIcon fontSize="small" />
					</IconButton>
					<Typography fontFamily="Gilroy-Bold" fontSize="18px">
						{isEdit ? "Edit Application Details" : "Add New K8s Application"}
					</Typography>
				</Box>
			</Box>
			<Grid container>
				<Grid item xs={1}></Grid>
				<Grid item xs={10}>
					{((isEdit && application.id) || !isEdit) && (
						<Formik
							initialValues={{
								applicationName: isEdit ? application.applicationName : "",
								gcpApplicationViewers: isEdit ? application.gcpApplicationViewers : [],
								cluster: isEdit ? application?.cluster?.clusterName : "",
								serviceRequesters: isEdit ? application.serviceRequesters : [],
								costLabelPairs: isEdit
									? application?.costLabelPairs?.filter(
											(e: any) => !["department", "cost-center", "cluster-owner"].includes(e.cost)
									  )
									: [],
								environment: isEdit ? application.environment : "",
								costCenterValue: isEdit
									? application.costLabelPairs?.find((elem: any) => elem.cost == "cost-center")
											.label
									: "",
							}}
							validationSchema={validationSchema}
							onSubmit={async ({
								applicationName,
								gcpApplicationViewers,
								cluster,
								serviceRequesters,
								costLabelPairs,
								environment,
								costCenterValue,
							}) => {
								// console.log({
								// 	applicationName,
								// 	gcpApplicationViewers,
								// 	cluster,
								// 	serviceRequesters,
								// 	costLabelPairs,
								// 	environment,
								// 	costCenterValue,
								// });
								if (!isEdit) {
									let keyValuePairs = [];
									keyValuePairs.push({
										cost: "cost-center",
										label: costCenterValue,
									});
									keyValuePairs.push(
										...costLabelPairs.map((e: any) => {
											return { cost: e.cost, label: e.label };
										})
									);
									const data = await queryGraphql(
										`
					                            mutation CreateApplication($input: ApplicationCreateInput) {
					  createApplication(input: $input) {
					    id
					  }
					}
					    `,
										{
											input: {
												applicationName,
												gcpApplicationViewers,
												cluster: clusters.filter(
													(e: any) => e.clusterName == cluster && e.environment == environment
												)[0]["id"],
												serviceRequesters,
												environment,
												costLabelPairs: keyValuePairs,
											},
										}
									);

									const info = await queryGraphql(`mutation Mutation($input: ResourceStatusCreateInput) {
										createResourceStatus(input: $input) {
											id
										}
										}`, {
											input: {
											  currentStatus: "pending",
											  resourceEnvironment: environment,
											  resourceId: data.createApplication.id,
											  resourceName: applicationName,
											  resourceType: "application"
											}
										  });

																		


									if (data.createApplication.id && info.createResourceStatus.id) {
										navigate("/applications");
									}
									return;
								} else {
									let keyValuePairs = [];
									keyValuePairs.push({
										cost: "cost-center",
										label: costCenterValue,
									});
									keyValuePairs.push(
										...costLabelPairs.map((e: any) => {
											return { cost: e.cost, label: e.label };
										})
									);
									const data = await queryGraphql(
										`
					                           mutation UpdateApplication($input: ApplicationUpdateInput) {
								updateApplication(input: $input) {
								  id
								}
							  }
					    `,
										{
											input: {
												id: id,

												applicationName,
												gcpApplicationViewers,
												cluster: clusters.filter(
													(e: any) => e.clusterName == cluster && e.environment == environment
												)[0]["id"],
												serviceRequesters,
												environment,
												costLabelPairs: keyValuePairs,
											},
										}
									);
									const info = await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) {
										updateResourceStatus(input: $input) {
											id
										}
										}`, {
											input: {
											  currentStatus: "pending",
											  resourceId: id,
											  resourceName: applicationName
											}
										  });
									
									if (data.updateApplication.id && info.updateResourceStatus.id) {
										navigate("/applications");
									}
									return;
								}
							}}
						>
							{({
								handleChange,
								handleBlur,
								values,
								setFieldValue,
								errors,
								touched,
								isValid,
								dirty,
							}) => {
								return (
									<Form id="main-form">
										<Box>
											<Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
												{isEdit
													? "Make changes to below details and save"
													: "Fill the below details to add application"}
											</Typography>
											<div
												style={{
													backgroundColor: "white",
													padding: "24px",
													borderRadius: "8px",
													boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
													marginBottom: "24px",
												}}
											>
												<Typography
													fontFamily="Gilroy-Medium"
													fontSize="14px"
													style={{ marginBottom: "16px" }}
												>
													Basic Information
												</Typography>
												<Divider sx={{ marginBottom: "24px" }} />
												<Grid container spacing={4} style={{ marginBottom: "16px" }}>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Application Name" />
														<TextField
															sx={{
																"& .MuiFilledInput-root": {
																	border:
																		errors["applicationName"] && touched["applicationName"]
																			? "1px solid red"
																			: "1px solid #00000000",

																	"&.Mui-focused": {
																		backgroundColor: "white",
																		border: "1px solid black",
																	},
																},
															}}
															variant="filled"
															fullWidth
															name="applicationName"
															value={values.applicationName}
															// onChange={handleChange}
															onChange={e =>{
																handleChange(e);
																setCurrentApplication(e.target.value);
															}}
															onBlur={handleBlur}
															placeholder="Enter name here"
															size="medium"
															hiddenLabel
															InputProps={{
																disableUnderline: true,

																style: {
																	borderRadius: "8px",
																},
															}}
														/>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="applicationName" component="div" />
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Cluster" />
														<Autocomplete
															options={clustersNamesList}
															// getOptionLabel={(option) => option || ""}
															filterSelectedOptions
															// defaultValue={values.cluster}
															value={values.cluster || null} 
															onChange={(e, newVal, reason) => {
																// setApplicationOwners(newVal);
																// console.log(newVal);
																
																	setFieldValue("cluster", newVal);
																	setCurrentCluster(newVal);
					

															}}													
															renderInput={(params) => (
																<TextField
																	{...params}
																	variant="filled"
																	fullWidth
																	placeholder="Select"
																	size="medium"
																	hiddenLabel
																	sx={{
																		"& .MuiFilledInput-root": {
																			border:
																				errors["cluster"] && touched["cluster"]
																					? "1px solid red"
																					: "1px solid #00000000",
																			"&.Mui-focused": {
																				backgroundColor: "white",
																				border: "1px solid black",
																			},
																		},
																	}}
																	InputProps={{
																		...params.InputProps,
																		style: {
																			borderRadius: "8px",
																		},

																		disableUnderline: true,
																	}}
																/>
															)}
														/>

														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															{getIn(errors, `cluster`)}
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Environment" />
														{/* <Box display="flex" gap={2} mt="14px">
															<Button
																disabled
																sx={{
																	borderRadius: 5,
																	borderColor: values.environment == "dev" ? "#000000" : "#666666",
																	color: values.environment == "dev" ? "#000000" : "#666666",
																	bgcolor: values.environment == "dev" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"
																// onClick={() => setFieldValue("environment", "dev")}
															>
																dev
															</Button>
															<Button
																disabled
																sx={{
																	borderRadius: 5,
																	borderColor:
																		values.environment == "staging" ? "#000000" : "#666666",
																	color: values.environment == "staging" ? "#000000" : "#666666",
																	bgcolor: values.environment == "staging" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"

																// onClick={() => setFieldValue("environment", "staging")}
															>
																staging
															</Button>
															<Button
																disabled
																sx={{
																	borderRadius: 5,
																	borderColor: values.environment == "prod" ? "#000000" : "#666666",
																	color: values.environment == "prod" ? "#000000" : "#666666",
																	bgcolor: values.environment == "prod" ? "#E0E0E0" : "#FFFFFF",
																	textTransform: "none",
																}}
																variant="outlined"

																// onClick={() => setFieldValue("environment", "prod")}
															>
																prod
															</Button>
														</Box> */}
														<TextField
															select
															placeholder="Select"
															name="environment"
															value={values.environment}
															onChange={handleChange}
															onBlur={handleBlur}
															variant="filled"
															fullWidth
															disabled={values.cluster ? false : true}
															hiddenLabel
															size="medium"
															sx={{
																"& .MuiFilledInput-root": {
																	border:
																		errors["environment"] && touched["environment"]
																			? "1px solid red"
																			: "1px solid #00000000",
																	"&.Mui-focused": {
																		backgroundColor: "white",
																		border: "1px solid black",
																	},
																},
															}}
															InputProps={{
																disableUnderline: true,
																style: {
																	borderRadius: "8px",
																},
															}}
														>
															{clusters
																.filter((p:any) => 
																	p.clusterName === values.cluster &&
																	!allApps.some(app => app.applicationName === currentApplication && app.environment === p.environment)
																  )
																.map((e: any) => e.environment)
																.filter(e => !devEnvChecker? e === "dev": e !=='dev')
																.map((e) => (
																	<MenuItem
																		value={e}
																		key={e}
																		onClick={() => setFieldValue("environment", e)}
																	>
																		{e}
																	</MenuItem>
																))}
														</TextField>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="environment" component="div" />
														</div>
													</Grid>
												</Grid>
											</div>
											<div
												style={{
													backgroundColor: "white",
													padding: "24px",
													borderRadius: "8px",
													boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
													marginBottom: "24px",
												}}
											>
												<Typography
													fontFamily="Gilroy-Medium"
													fontSize="14px"
													style={{ marginBottom: "16px" }}
												>
													Application Viewers and Service Owners
												</Typography>
												<Divider sx={{ marginBottom: "24px" }} />
												<Grid container spacing={4} style={{ marginBottom: "16px" }}>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Application Viewers" />
														<Autocomplete
															multiple
															// options={[].map((e: any) => e.email)}
															options={users.map((e: any) => e.email)}
															filterSelectedOptions
															ChipProps={{ size: "small" }}
															freeSolo
															defaultValue={values.gcpApplicationViewers}
															onChange={(e, newVal, reason) => {
																// setApplicationOwners(newVal);
																setFieldValue("gcpApplicationViewers", newVal);
															}}
															renderInput={(params) => (
																<TextField
																	{...params}
																	variant="filled"
																	fullWidth
																	placeholder="e.g. abc121@gmail.com"
																	size="small"
																	hiddenLabel
																	InputProps={{
																		...params.InputProps,
																		style: {
																			borderRadius: "8px",
																		},
																		disableUnderline: true,
																	}}
																/>
															)}
														/>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															{typeof getIn(errors, `gcpApplicationViewers`) == "string"
																? getIn(errors, `gcpApplicationViewers`)
																: (getIn(errors, `gcpApplicationViewers`) as string[])?.filter(
																		(e) => e != null
																  )[0]}
														</div>
													</Grid>
													<Grid item xs={12} sm={6}>
														<CompulsoryFieldsLabel fieldName="Service Owners" />
														<Autocomplete
															multiple
															options={users.map((e: any) => e.email)}
															// options={[].map((e: any) => e.email)}
															filterSelectedOptions
															ChipProps={{ size: "small" }}
															freeSolo
															defaultValue={values.serviceRequesters}
															onChange={(e, newVal, reason) => {
																// setApplicationOwners(newVal);

																setFieldValue("serviceRequesters", newVal);
															}}
															renderInput={(params) => (
																<TextField
																	{...params}
																	variant="filled"
																	fullWidth
																	placeholder="e.g. abc121@gmail.com"
																	size="small"
																	hiddenLabel
																	InputProps={{
																		...params.InputProps,
																		style: {
																			borderRadius: "8px",
																		},

																		disableUnderline: true,
																	}}
																/>
															)}
														/>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															{typeof getIn(errors, `serviceRequesters`) == "string"
																? getIn(errors, `serviceRequesters`)
																: (getIn(errors, `serviceRequesters`) as string[])?.filter(
																		(e) => e != null
																  )[0]}
														</div>
													</Grid>
												</Grid>
											</div>
											<div
												style={{
													backgroundColor: "white",
													padding: "24px",
													borderRadius: "8px",
													boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
													marginBottom: "24px",
												}}
											>
												<Typography
													fontFamily="Gilroy-Medium"
													fontSize="14px"
													style={{ marginBottom: "16px" }}
												>
													Application Tags / Labels
												</Typography>
												<Divider sx={{ marginBottom: "24px" }} />
												<Grid container spacing={4} style={{ marginBottom: "16px" }}>
													<Grid item xs={12} sm={6}>
														<Typography
															gutterBottom
															fontFamily="Gilroy-Regular"
															fontSize="14px"
															ml="4px"
														>
															Key
														</Typography>
													</Grid>
													<Grid item xs={12} sm={6}>
														<Typography
															gutterBottom
															fontFamily="Gilroy-Regular"
															fontSize="14px"
															ml="4px"
														>
															Value
														</Typography>
													</Grid>
												</Grid>
												<Grid container spacing={4}>
													<Grid item xs={12} sm={6}>
														<TextField
															fullWidth
															placeholder="Enter key"
															size="medium"
															hiddenLabel
															disabled={true}
															value="cost-center"
															variant="filled"
															sx={{ marginBottom: "0px" }}
															InputProps={{
																disableUnderline: true,
																style: {
																	borderRadius: "8px",
																},
															}}
														/>
													</Grid>
													<Grid item xs={12} sm={6}>
														<Box display="flex" alignItems="center" justifyContent="space-between">
															<TextField
																fullWidth
																placeholder="Enter value"
																size="medium"
																value={values.costCenterValue}
																hiddenLabel
																variant="filled"
																name="costCenterValue"
																sx={{
																	"& .MuiFilledInput-root": {
																		border:
																			errors["costCenterValue"] && touched["costCenterValue"]
																				? "1px solid red"
																				: "1px solid #00000000",
																		"&.Mui-focused": {
																			backgroundColor: "white",
																			border: "1px solid black",
																		},
																	},
																	marginBottom: "0px",
																}}
																onChange={handleChange}
																onBlur={handleBlur}
																InputProps={{
																	disableUnderline: true,
																	style: {
																		borderRadius: "8px",
																	},
																}}
															/>
														</Box>
														<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
															<ErrorMessage name="costCenterValue" component="div" />
														</div>
													</Grid>
												</Grid>
												<Grid container spacing={4} mt="8px">
													{values.costLabelPairs?.map((e: any, index: number) => (
														<>
															<Grid item xs={12} sm={6} key={index}>
																<TextField
																	fullWidth
																	placeholder="Enter key"
																	size="medium"
																	hiddenLabel
																	value={e.cost}
																	sx={{
																		"& .MuiFilledInput-root": {
																			border: "1px solid #00000000",
																			"&.Mui-focused": {
																				backgroundColor: "white",
																				border: "1px solid black",
																			},
																		},
																		marginBottom: "0px",
																	}}
																	variant="filled"
																	onChange={(event) => {
																		setFieldValue("costLabelPairs", [
																			...values.costLabelPairs.map((pair: any) => {
																				if (e.id === pair.id) {
																					pair.cost = event.target.value || "";
																				}
																				return pair;
																			}),
																		]);
																	}}
																	InputProps={{
																		disableUnderline: true,
																		style: {
																			borderRadius: "8px",
																		},
																	}}
																/>
																<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
																	{getIn(errors, `costLabelPairs[${index}].cost`)}
																</div>
															</Grid>
															<Grid item xs={12} sm={6}>
																<Box
																	display="flex"
																	alignItems="center"
																	justifyContent="space-between"
																>
																	<TextField
																		fullWidth
																		placeholder="Enter value"
																		size="medium"
																		sx={{
																			"& .MuiFilledInput-root": {
																				border: "1px solid #00000000",
																				"&.Mui-focused": {
																					backgroundColor: "white",
																					border: "1px solid black",
																				},
																			},
																			marginBottom: "0px",
																		}}
																		value={e.label}
																		hiddenLabel
																		variant="filled"
																		onChange={(event) => {
																			setFieldValue("costLabelPairs", [
																				...values.costLabelPairs.map((pair: any) => {
																					if (e.id === pair.id) {
																						pair.label = event.target.value || "";
																					}
																					return pair;
																				}),
																			]);
																		}}
																		InputProps={{
																			disableUnderline: true,
																			style: {
																				borderRadius: "8px",
																			},
																		}}
																	/>

																	<Button
																		sx={{ marginLeft: "8px" }}
																		onClick={() => {
																			let clpArr = values.costLabelPairs.filter((pair: any) => {
																				return pair.id != e.id;
																			});

																			setFieldValue("costLabelPairs", clpArr);
																		}}
																	>
																		<img alt="close-icon" src="/assets/close-icon.svg" />
																	</Button>
																</Box>
																<div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
																	{getIn(errors, `costLabelPairs[${index}].label`)}
																</div>
															</Grid>
														</>
													))}
												</Grid>
												<Button
													variant="text"
													sx={{ color: "#000000", textTransform: "none", mt: "16px" }}
													onClick={() =>
														setFieldValue("costLabelPairs", [
															...values.costLabelPairs,
															{ cost: "", label: "", id: crypto.randomUUID() },
														])
													}
												>
													+ Add Key Value Pair
												</Button>
											</div>
											<Box display="flex" justifyContent="flex-end" gap={2}>
												<Button
													sx={{
														color: "black",
														borderColor: "black",
														fontWeight: "bold",
														textTransform: "none",
														px: 4,
													}}
													variant="outlined"
													size="large"
													onClick={() => navigate("/clusters")}
												>
													Cancel
												</Button>
												<Button
													sx={{
														backgroundColor: "black",
														color: "white",
														fontWeight: "bold",
														"&:hover": {
															backgroundColor: "white",
															color: "black",
															borderColor: "black",
														},
														textTransform: "none",
														px: 4,
													}}
													variant="contained"
													size="large"
													type="submit"
													disabled={!(isValid && dirty) || isViewer}
													//onClick={handleOpenModal}
												>
													{isEdit ? "Save Changes" : "Add New Application"}
												</Button>
											</Box>
										</Box>
									</Form>
								);
							}}
						</Formik>
					)}
				</Grid>
				<Grid item xs={1}></Grid>
			</Grid>
             
            
		</Container>
	);
};

//export default ApplicationRequesterForm;
const ApplicationRequesterForm: React.FC<ApplicationRequesterFormProps> = ({ isEdit }) => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    // Using your remembered calculation for marginLeft
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;
	const copyIsEdit = isEdit;
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                // Assuming "integrations" is the correct index for Kyverno page
                // If Kyverno has its own top-level menu item, update this index.
                index={"applications"} // Or a more specific index like "integrations-kyverno" if applicable
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // bgcolor: '#fafafa', // bgcolor commented out as per your previous change
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    // Using your remembered unit and calculation for marginLeft
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <ApplicationRequesterFormContent isEdit={copyIsEdit} />
            </Box>
        </Box>
    );
};

export default ApplicationRequesterForm;
// import {
// 	Autocomplete,
// 	Box,
// 	Breadcrumbs,
// 	Button,
// 	Container,
// 	Divider,
// 	Grid,
// 	IconButton,
// 	Link,
// 	MenuItem,
// 	Modal,
// 	TextField,
// 	Typography,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import React, { useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { ErrorMessage, Form, Formik, getIn } from "formik";
// import * as Yup from "yup";
// import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
// import queryGraphql from "../../helpers/ApiInterface";
// import { useAuth } from "../../helpers/AuthProvider";

// interface ApplicationRequesterFormProps {
// 	isEdit: boolean;
// }
// interface AppData{
// 	applicationName: string;
// 	environment: string;
// }
// interface OneApplication {
// 	id: string;
// 	applicationName: string;
// 	status: string;
// 	environment: string;
//   }
// interface OneCluster {
// 	id: string;
// 	clusterName: string;
// 	environment: string;
// 	status: string; // Or a more specific type if you know the possible values (e.g., enum)
// 	applications: OneApplication[];
//   }
//   const modalStyle = {
//     position: 'absolute',
//     top: '50%',
//     left: '50%',
//     transform: 'translate(-50%, -50%)',
//     width: { xs: '90%', sm: 450 }, // Responsive width
//     bgcolor: 'background.paper',
//     border: '1px solid #ccc',
//     borderRadius: '8px',
//     boxShadow: 24,
//     p: 4,
// };
// const validationSchema = Yup.object().shape({
// 	applicationName: Yup.string()
// 		.required("This field is required")
// 		.strict()
// 		.lowercase("This field must be in lowercase")
// 		.matches(/^[a-zA-Z0-9\s]*$/, "This field must be alphanumeric(lowercase and numbers only)")
// 		.max(20, "This field must not exceed 20 characters"),
// 	gcpApplicationViewers: Yup.array()
// 		.of(
// 			Yup.string()
// 				.required("This field is required")
// 				.matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain")
// 				.strict()
// 		)
// 		.required("This field is required")
// 		.min(1, "This field is required")
// 		.strict(),
// 	cluster: Yup.string().required("This field is required").strict(),

// 	serviceRequesters: Yup.array()
// 		.of(
// 			Yup.string()
// 				.required("This field is required")
// 				.matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain")
// 				.strict()
// 		)
// 		.required("This field is required")
// 		.min(1, "This field is required")
// 		.strict(),
// 	costLabelPairs: Yup.array()
// 		.of(
// 			Yup.object({
// 				cost: Yup.string()
// 					.required("This field is required")
// 					.strict()
// 					.lowercase("This field must be in lowercase")
// 					.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
// 					.max(20, "This field must not exceed 20 characters"),
// 				label: Yup.string()
// 					.required("This field is required")
// 					.strict()
// 					.lowercase("This field must be in lowercase")
// 					.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
// 					.max(20, "This field must not exceed 20 characters"),
// 			})
// 		)
// 		.required("This field is required"),
// 	environment: Yup.string().required("This field is required").oneOf(["dev", "staging", "prod"]),
// 	costCenterValue: Yup.string()
// 		.required("This field is required")
// 		.strict()
// 		.lowercase("This field must be in lowercase")
// 		.matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
// 		.max(20, "This field must not exceed 20 characters"),
// });
// const ApplicationRequesterForm: React.FC<ApplicationRequesterFormProps> = ({ isEdit }) => {
// 	const navigate = useNavigate();
// 	const [application, setApplication] = useState<any>({});
	
// 	const [currentCluster,setCurrentCluster] = useState<any>([]);
// 	const [devEnvChecker,setDevEnvChecker] = useState<boolean>(false);
// 	const [dummyEnvs, setDummyEnvs] = useState<any>([]);
// 	const [currentApplication, setCurrentApplication] = useState<any>();
// 	const [allApps,setAllApps] = useState<AppData[]>([]);
// 	const [clusters, setClusters] = useState([]);
// 	const [clustersNamesList, setClustersNamesList] = useState<any>([]);
// 	const [users, setUsers] = useState([]);
// 	const { id } = useParams();

// 	const [applications,setApplications] = useState<any>([]);
// 	const [envs, setEnvs] = useState<any>([]);
// 	const [openModal, setOpenModal] = useState(false);
// 	const { user } = useAuth();
	
// 	const addAllApps = (data1: string, data2: string) =>{
// 		if(data1== currentApplication && data2== 'dev')
// 			setDevEnvChecker(true);
// 		setAllApps((prevItems) => [...prevItems,{applicationName: data1, environment: data2}])
// 	}

// 	React.useEffect(() => {
//         if (currentCluster) { 
//             setDummyEnvs(currentCluster);
//             // console.log("dummyEnvs updated", dummyEnvs);
//         }
//     }, [currentCluster,envs]);

// 	// This chunk of code input dummy values in dummy varaible and this is used to re render the clusters data useffect to render again and again every second to manage all the asynchronous calls and efficiently help the user input correct data in the application requester form 
// 	React.useEffect(() => {
// 		const intervalId = setInterval(() => {
// 		  setDummyEnvs(new Date().toLocaleTimeString());
// 		}, 2000);
	
// 		return () => {
// 		  clearInterval(intervalId);
// 		};
// 	  }, []);
// 	React.useEffect(() => {
// 		(async () => {
// 			try {
// 				const data = await queryGraphql(`
//                         query{
//                         users {
// 						id,
//                         email
//                         }
//                         }
// 					`);
// 				if (data.users) {
// 					setUsers(data.users);
// 				}
// 			} catch (error) {
// 				console.log(error);
// 			}
// 		})();
// 	}, []);
// 	React.useEffect(() => {
// 		(async () => {
// 			try {
// 				const data = await queryGraphql(
// 					`
//                        query{
//                         clusters {
// 						id,
//                         clusterName,
//                         environment,
// 						status,
// 						applications {
// 							id
// 							applicationName
// 							status
// 							environment
// 							}
//                         }
//                         }
// 					`
// 				);

// 				const data2 = await queryGraphql(`
// 					query Users {
// 							users {
// 								id,
// 								name,
// 								email,
// 								clustersViewable {
// 								clusterName,
// 								id,
// 								environment,
// 								}
// 								applicationsViewable {
// 								applicationName,
// 								id,
// 								environment,
// 								}
// 							}
// 							}
// 				`);
// 			// if(data2.users)	{
// 			// 	console.log("clsuter viewable", data2.users.filter((e:any) => e.email == user.email).map((e:any)=> e.clustersViewable).flat().map((e:any)=> e.id) );
// 			// }
// 				// console.log(data.clusters);
// 				if (data.clusters) {
// 					setClusters(data.clusters);
// 					setClustersNamesList(
// 						Array.from(
// 							new Set(
// 								data.clusters
// 									?.filter((e: any) => e.status == "active")
// 									.filter((e: any)=>  data2.users?.filter((e:any) => e.email == user.email).map((e:any)=> e.clustersViewable).flat().map((e:any)=> e.id).includes(e.id))
// 									.map((e: any) => e.clusterName)
// 							)
// 						)
// 					);
					
				
// 				// setEnvs([]);
// 				setApplications([]);
// 				setAllApps([]);
// 				setDevEnvChecker(false);
// 				data.clusters.map((p: OneCluster) => {
// 					// console.log("p.clusterName->",p.clusterName);
// 					// console.log('currentCluster->', currentCluster);
// 					if(p.clusterName === currentCluster){
						
// 						p.applications.map((e: OneApplication)=>((e.status == "active" || e.status == "pending") ? addAllApps(e.applicationName,e.environment): null));
// 						// console.log("envs->",envs);
						
// 					}
// 					// console.log(p.clusterName);
// 					// console.log("Applications",applications);
// 					// console.log(allApps);
// 				}
// 			);
// 			// console.log("applications->",applications);
// 			// console.log("platfotm->", currentCluster)
// 			// console.log("envs->",envs);
// 				}
// 			} catch (error) {
// 				console.log(error);
// 			}
// 		})();
// 	}, [currentApplication,currentCluster,dummyEnvs]);
// 	React.useEffect(() => {
// 		(async () => {
// 			try {
// 				const data = await queryGraphql(
// 					`
//                         query($applicationId: String!){
//                         application(id:$applicationId) {
// 						id,
//                         applicationName,
// 						gcpApplicationViewers,
// 						cluster{
// 						id,
// 						clusterName,
// 						environment
// 						},
// 						serviceRequesters,
// 						costLabelPairs{
// 						cost,
// 						label
// 						},
// 						environment,
		
//                         }
//                         }
// `,
// 					{ applicationId: id }
// 				);
// 				if (data.application) {
// 					setApplication(data.application);
// 				}
// 			} catch (error) {
// 				console.log(error);
// 			}
// 		})();
// 	}, []);

// 	return (
// 		<Container
// 			disableGutters
// 			maxWidth={false}
// 			sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
// 		>
// 			<Box sx={{ mb: 4 }}>
// 				<Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
// 					<Link underline="hover" color="inherit" href="/applications">
// 						Applications
// 					</Link>
// 					<Typography color="text.primary">
// 						{isEdit ? "Edit Application Details" : "Add New Application"}
// 					</Typography>
// 				</Breadcrumbs>
// 				<Box display="flex" alignItems="center" mt={2}>
// 					<IconButton
// 						edge="start"
// 						aria-label="back"
// 						color="inherit"
// 						onClick={() => navigate("/applications")}
// 					>
// 						<ArrowBackIosNewIcon fontSize="small" />
// 					</IconButton>
// 					<Typography fontFamily="Gilroy-Bold" fontSize="18px">
// 						{isEdit ? "Edit Application Details" : "Add New Application"}
// 					</Typography>
// 				</Box>
// 			</Box>
// 			<Grid container>
//             <Grid item xs={1}></Grid>
//             <Grid item xs={10}>
//                 {((isEdit && application?.id) || !isEdit) && (
//                     <Formik
//                         initialValues={{
//                             applicationName: isEdit ? application.applicationName : '',
//                             gcpApplicationViewers: isEdit ? application.gcpApplicationViewers : [],
//                             cluster: isEdit ? application.cluster.clusterName : '',
//                             serviceRequesters: isEdit ? application.serviceRequesters : [],
//                             costLabelPairs: isEdit
//                                 ? application?.costLabelPairs?.filter(
//                                       (e) => !['department', 'cost-center', 'cluster-owner'].includes(e.cost)
//                                   ) || [] // Add fallback for safety
//                                 : [],
//                             environment: isEdit ? application.environment : '',
//                             costCenterValue: isEdit
//                                 ? application.costLabelPairs?.find((elem) => elem.cost == 'cost-center')
//                                       ?.label || '' // Add fallback for safety
//                                 : '',
//                         }}
//                         validationSchema={validationSchema}
//                         onSubmit={async ({
//                             applicationName,
//                             gcpApplicationViewers,
//                             cluster,
//                             serviceRequesters,
//                             costLabelPairs,
//                             environment,
//                             costCenterValue,
//                         }) => {
//                             // --- THIS SUBMIT LOGIC REMAINS UNCHANGED ---
//                             // It will be triggered by the modal's confirm button via handleSubmit()

//                             if (!isEdit) {
//                                 let keyValuePairs = [];
//                                 keyValuePairs.push({
//                                     cost: 'cost-center',
//                                     label: costCenterValue,
//                                 });
//                                 keyValuePairs.push(
//                                     ...costLabelPairs.map((e) => {
//                                         return { cost: e.cost, label: e.label };
//                                     })
//                                 );
//                                 try { // Added basic try-catch
//                                     const data = await queryGraphql(
//                                         `
//                                             mutation CreateApplication($input: ApplicationCreateInput) {
//                                                 createApplication(input: $input) {
//                                                     id
//                                                 }
//                                             }
//                                         `,
//                                         {
//                                             input: {
//                                                 applicationName,
//                                                 gcpApplicationViewers,
//                                                 cluster: clusters.find( // Use find for clarity
//                                                     (e) => e.clusterName == cluster && e.environment == environment
//                                                 )?.id, // Add optional chaining
//                                                 serviceRequesters,
//                                                 environment,
//                                                 costLabelPairs: keyValuePairs,
//                                             },
//                                         }
//                                     );

//                                     const info = await queryGraphql(`mutation Mutation($input: ResourceStatusCreateInput) {
//                                         createResourceStatus(input: $input) {
//                                             id
//                                         }
//                                         }`, {
//                                             input: {
//                                               currentStatus: "pending",
//                                               resourceEnvironment: environment,
//                                               resourceId: data.createApplication.id,
//                                               resourceName: applicationName,
//                                               resourceType: "application"
//                                             }
//                                           });

//                                     if (data?.createApplication?.id && info?.createResourceStatus?.id) {
//                                         navigate('/applications');
//                                     } else {
//                                         console.error("Failed to create application or resource status", data, info);
//                                         // Handle error (e.g., show a notification to the user)
//                                     }
//                                 } catch(error) {
//                                     console.error("Error during application creation:", error);
//                                      // Handle error (e.g., show a notification to the user)
//                                 }
//                                 return; // Keep return here
//                             } else {
//                                 let keyValuePairs = [];
//                                 keyValuePairs.push({
//                                     cost: 'cost-center',
//                                     label: costCenterValue,
//                                 });
//                                 keyValuePairs.push(
//                                     ...costLabelPairs.map((e) => {
//                                         return { cost: e.cost, label: e.label };
//                                     })
//                                 );
//                                 try { // Added basic try-catch
//                                     const data = await queryGraphql(
//                                         `
//                                            mutation UpdateApplication($input: ApplicationUpdateInput) {
//                                                 updateApplication(input: $input) {
//                                                 id
//                                                 }
//                                             }
//                                         `,
//                                         {
//                                             input: {
//                                                 id: id,
//                                                 applicationName,
//                                                 gcpApplicationViewers,
//                                                 cluster: clusters.find( // Use find for clarity
//                                                     (e) => e.clusterName == cluster && e.environment == environment
//                                                 )?.id, // Add optional chaining
//                                                 serviceRequesters,
//                                                 environment,
//                                                 costLabelPairs: keyValuePairs,
//                                             },
//                                         }
//                                     );

//                                     const info = await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) {
//                                         updateResourceStatus(input: $input) {
//                                             id
//                                         }
//                                         }`, {
//                                             input: {
//                                               currentStatus: "pending",
//                                               resourceId: id,
//                                               resourceName: applicationName
//                                             }
//                                           });

//                                     if (data?.updateApplication?.id && info?.updateResourceStatus?.id) {
//                                         navigate('/applications'); // Or navigate back to the specific application page
//                                     } else {
//                                          console.error("Failed to update application or resource status", data, info);
//                                         // Handle error (e.g., show a notification to the user)
//                                     }
//                                 } catch (error) {
//                                     console.error("Error during application update:", error);
//                                     // Handle error (e.g., show a notification to the user)
//                                 }
//                                 return; // Keep return here
//                             }
//                         }}
//                     >
//                         {({
//                             handleChange,
//                             handleBlur,
//                             values,
//                             setFieldValue,
//                             errors,
//                             touched,
//                             isValid,
//                             dirty,
//                             handleSubmit, // <-- Get Formik's handleSubmit function
//                         }) => {
//                             return (
//                                 <Form>
//                                     <Box>
//                                         <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
//                                             {isEdit
//                                                 ? 'Make changes to below details and save'
//                                                 : 'Fill the below details to add application'}
//                                         </Typography>

//                                         {/* --- Basic Information Section --- */}
//                                         <div
//                                             style={{
//                                                 backgroundColor: 'white',
//                                                 padding: '24px',
//                                                 borderRadius: '8px',
//                                                 boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
//                                                 marginBottom: '24px',
//                                             }}
//                                         >
//                                             <Typography
//                                                 fontFamily="Gilroy-Medium"
//                                                 fontSize="14px"
//                                                 style={{ marginBottom: '16px' }}
//                                             >
//                                                 Basic Information
//                                             </Typography>
//                                             <Divider sx={{ marginBottom: '24px' }} />
//                                             <Grid container spacing={4} style={{ marginBottom: '16px' }}>
//                                                 <Grid item xs={12} sm={6}>
//                                                     {/* <CompulsoryFieldsLabel fieldName="Application Name" /> */}
//                                                     <Typography gutterBottom fontFamily="Gilroy-Medium" fontSize="14px" ml="4px">Application Name <span style={{color: 'red'}}>*</span></Typography> {/* Example Label */}
//                                                     <TextField
//                                                         sx={{
//                                                             '& .MuiFilledInput-root': {
//                                                                 border:
//                                                                     errors['applicationName'] && touched['applicationName']
//                                                                         ? '1px solid red'
//                                                                         : '1px solid #00000000',
//                                                                 '&.Mui-focused': {
//                                                                     backgroundColor: 'white',
//                                                                     border: '1px solid black',
//                                                                 },
//                                                             },
//                                                         }}
//                                                         variant="filled"
//                                                         fullWidth
//                                                         name="applicationName"
//                                                         value={values.applicationName}
//                                                         onChange={e => {
//                                                             handleChange(e);
//                                                             setCurrentApplication(e.target.value); // Keep if needed elsewhere
//                                                         }}
//                                                         onBlur={handleBlur}
//                                                         placeholder="Enter name here"
//                                                         size="medium"
//                                                         hiddenLabel
//                                                         InputProps={{
//                                                             disableUnderline: true,
//                                                             style: { borderRadius: '8px' },
//                                                         }}
//                                                     />
//                                                     <div style={{ color: 'red', marginTop: '8px', marginLeft: '4px' }}>
//                                                         <ErrorMessage name="applicationName" component="div" />
//                                                     </div>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={6}>
//                                                      {/* <CompulsoryFieldsLabel fieldName="Cluster" /> */}
//                                                       <Typography gutterBottom fontFamily="Gilroy-Medium" fontSize="14px" ml="4px">Cluster <span style={{color: 'red'}}>*</span></Typography> {/* Example Label */}
//                                                     <Autocomplete
//                                                         options={clustersNamesList}
//                                                         filterSelectedOptions
//                                                         value={values.cluster || null}
//                                                         onChange={(e, newVal) => { // Simplified onChange
//                                                             setFieldValue('cluster', newVal || ""); // Set to empty string if null
//                                                             setCurrentCluster(newVal || ""); // Keep if needed elsewhere
//                                                             // Reset environment if cluster changes
//                                                             if (values.cluster !== newVal) {
//                                                                 setFieldValue('environment', '');
//                                                             }
//                                                         }}
//                                                         renderInput={(params) => (
//                                                             <TextField
//                                                                 {...params}
//                                                                 variant="filled"
//                                                                 fullWidth
//                                                                 placeholder="Select"
//                                                                 size="medium"
//                                                                 hiddenLabel
//                                                                 sx={{
//                                                                     '& .MuiFilledInput-root': {
//                                                                         border:
//                                                                             errors['cluster'] && touched['cluster']
//                                                                                 ? '1px solid red'
//                                                                                 : '1px solid #00000000',
//                                                                         '&.Mui-focused': {
//                                                                             backgroundColor: 'white',
//                                                                             border: '1px solid black',
//                                                                         },
//                                                                     },
//                                                                 }}
//                                                                 InputProps={{
//                                                                     ...params.InputProps,
//                                                                     style: { borderRadius: '8px' },
//                                                                     disableUnderline: true,
//                                                                 }}
//                                                             />
//                                                         )}
//                                                     />
//                                                     <div style={{ color: 'red', marginTop: '8px', marginLeft: '4px' }}>
//                                                         {/* Using touched and errors directly for Autocomplete */}
//                                                         {touched.cluster && errors.cluster ? String(errors.cluster) : ''}
//                                                     </div>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={6}>
//                                                       {/* <CompulsoryFieldsLabel fieldName="Environment" /> */}
//                                                       <Typography gutterBottom fontFamily="Gilroy-Medium" fontSize="14px" ml="4px">Environment <span style={{color: 'red'}}>*</span></Typography> {/* Example Label */}
//                                                     <TextField
//                                                         select
//                                                         placeholder="Select"
//                                                         name="environment"
//                                                         value={values.environment}
//                                                         onChange={(e) => { // Use specific handler for select
//                                                             handleChange(e); // Update formik state
//                                                             setFieldValue("environment", e.target.value); // Explicitly set value
//                                                         }}
//                                                         onBlur={handleBlur}
//                                                         variant="filled"
//                                                         fullWidth
//                                                         disabled={!values.cluster} // Disable if no cluster selected
//                                                         hiddenLabel
//                                                         size="medium"
//                                                         sx={{
//                                                             '& .MuiFilledInput-root': {
//                                                                 border:
//                                                                     errors['environment'] && touched['environment']
//                                                                         ? '1px solid red'
//                                                                         : '1px solid #00000000',
//                                                                 '&.Mui-focused': {
//                                                                     backgroundColor: 'white',
//                                                                     border: '1px solid black',
//                                                                 },
//                                                             },
//                                                         }}
//                                                         InputProps={{
//                                                             disableUnderline: true,
//                                                             style: { borderRadius: '8px' },
//                                                         }}
//                                                         SelectProps={{
//                                                             displayEmpty: true // Ensures placeholder shows if value is empty
//                                                         }}
//                                                     >
//                                                         <MenuItem value="" disabled>
//                                                             Select Environment
//                                                         </MenuItem>
//                                                         {clusters // Filter environments based on selected cluster and availability
//                                                             ?.filter((p) =>
//                                                                 p.clusterName === values.cluster &&
//                                                                 (isEdit && p.environment === application?.environment || // Allow original environment in edit mode
//                                                                 !allApps?.some(app => app.applicationName === currentApplication && app.environment === p.environment)) // Check availability
//                                                             )
//                                                             .map((e) => e.environment)
//                                                             // .filter(e => !devEnvChecker ? e === "dev" : e !== 'dev') // Keep your devEnvChecker logic if needed
//                                                             .map((env) => (
//                                                                 <MenuItem
//                                                                     value={env}
//                                                                     key={env}
//                                                                     // Removed redundant onClick
//                                                                 >
//                                                                     {env}
//                                                                 </MenuItem>
//                                                             ))}
//                                                     </TextField>
//                                                     <div style={{ color: 'red', marginTop: '8px', marginLeft: '4px' }}>
//                                                         <ErrorMessage name="environment" component="div" />
//                                                     </div>
//                                                 </Grid>
//                                             </Grid>
//                                         </div>

//                                         {/* --- Application Viewers and Service Owners Section --- */}
//                                         <div
//                                             style={{
//                                                 backgroundColor: 'white',
//                                                 padding: '24px',
//                                                 borderRadius: '8px',
//                                                 boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
//                                                 marginBottom: '24px',
//                                             }}
//                                         >
//                                             <Typography
//                                                 fontFamily="Gilroy-Medium"
//                                                 fontSize="14px"
//                                                 style={{ marginBottom: '16px' }}
//                                             >
//                                                 Application Viewers and Service Owners
//                                             </Typography>
//                                             <Divider sx={{ marginBottom: '24px' }} />
//                                             <Grid container spacing={4} style={{ marginBottom: '16px' }}>
//                                                 <Grid item xs={12} sm={6}>
//                                                     {/* <CompulsoryFieldsLabel fieldName="Application Viewers" /> */}
//                                                     <Typography gutterBottom fontFamily="Gilroy-Medium" fontSize="14px" ml="4px">Application Viewers <span style={{color: 'red'}}>*</span></Typography> {/* Example Label */}
//                                                     <Autocomplete
//                                                         multiple
//                                                         options={users?.map((e) => e.email) || []} // Add fallback
//                                                         filterSelectedOptions
//                                                         ChipProps={{ size: 'small' }}
//                                                         freeSolo // Allows adding emails not in the list
//                                                         value={values.gcpApplicationViewers} // Use value instead of defaultValue for controlled component
//                                                         onChange={(e, newVal) => {
//                                                             setFieldValue('gcpApplicationViewers', newVal);
//                                                         }}
//                                                         renderInput={(params) => (
//                                                             <TextField
//                                                                 {...params}
//                                                                 variant="filled"
//                                                                 fullWidth
//                                                                 placeholder="Enter email(s) and press Enter"
//                                                                 size="small"
//                                                                 hiddenLabel
//                                                                 InputProps={{
//                                                                     ...params.InputProps,
//                                                                     style: { borderRadius: '8px' },
//                                                                     disableUnderline: true,
//                                                                 }}
//                                                             />
//                                                         )}
//                                                     />
//                                                      <div style={{ color: 'red', marginTop: '8px', marginLeft: '4px' }}>
//                                                         {/* Simplified error display for array fields */}
//                                                          {touched.gcpApplicationViewers && errors.gcpApplicationViewers ? String(errors.gcpApplicationViewers) : ''}
//                                                     </div>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={6}>
//                                                     {/* <CompulsoryFieldsLabel fieldName="Service Owners" /> */}
//                                                     <Typography gutterBottom fontFamily="Gilroy-Medium" fontSize="14px" ml="4px">Service Owners <span style={{color: 'red'}}>*</span></Typography> {/* Example Label */}
//                                                     <Autocomplete
//                                                         multiple
//                                                         options={users?.map((e) => e.email) || []} // Add fallback
//                                                         filterSelectedOptions
//                                                         ChipProps={{ size: 'small' }}
//                                                         freeSolo // Allows adding emails not in the list
//                                                         value={values.serviceRequesters} // Use value instead of defaultValue
//                                                         onChange={(e, newVal) => {
//                                                             setFieldValue('serviceRequesters', newVal);
//                                                         }}
//                                                         renderInput={(params) => (
//                                                             <TextField
//                                                                 {...params}
//                                                                 variant="filled"
//                                                                 fullWidth
//                                                                 placeholder="Enter email(s) and press Enter"
//                                                                 size="small"
//                                                                 hiddenLabel
//                                                                 InputProps={{
//                                                                     ...params.InputProps,
//                                                                     style: { borderRadius: '8px' },
//                                                                     disableUnderline: true,
//                                                                 }}
//                                                             />
//                                                         )}
//                                                     />
//                                                     <div style={{ color: 'red', marginTop: '8px', marginLeft: '4px' }}>
//                                                         {/* Simplified error display for array fields */}
//                                                         {touched.serviceRequesters && errors.serviceRequesters ? String(errors.serviceRequesters) : ''}
//                                                     </div>
//                                                 </Grid>
//                                             </Grid>
//                                         </div>

//                                         {/* --- Application Tags / Labels Section --- */}
//                                         <div
//                                             style={{
//                                                 backgroundColor: 'white',
//                                                 padding: '24px',
//                                                 borderRadius: '8px',
//                                                 boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
//                                                 marginBottom: '24px',
//                                             }}
//                                         >
//                                             <Typography
//                                                 fontFamily="Gilroy-Medium"
//                                                 fontSize="14px"
//                                                 style={{ marginBottom: '16px' }}
//                                             >
//                                                 Application Tags / Labels
//                                             </Typography>
//                                             <Divider sx={{ marginBottom: '24px' }} />
//                                             <Grid container spacing={4} style={{ marginBottom: '16px' }}>
//                                                 {/* Headers */}
//                                                 <Grid item xs={12} sm={6}>
//                                                     <Typography fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={5}> {/* Adjusted size */}
//                                                     <Typography fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Value</Typography>
//                                                 </Grid>
//                                                  <Grid item xs={12} sm={1}></Grid> {/* Spacer for delete button */}
//                                             </Grid>

//                                             {/* Cost Center Row */}
//                                             <Grid container spacing={4} alignItems="center"> {/* Use alignItems */}
//                                                 <Grid item xs={12} sm={6}>
//                                                     <TextField
//                                                         fullWidth
//                                                         size="medium"
//                                                         hiddenLabel
//                                                         disabled={true}
//                                                         value="cost-center"
//                                                         variant="filled"
//                                                         InputProps={{ disableUnderline: true, style: { borderRadius: '8px' } }}
//                                                     />
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={5}> {/* Adjusted size */}
//                                                     <TextField
//                                                         fullWidth
//                                                         placeholder="Enter value"
//                                                         size="medium"
//                                                         value={values.costCenterValue}
//                                                         hiddenLabel
//                                                         variant="filled"
//                                                         name="costCenterValue"
//                                                         sx={{
//                                                             '& .MuiFilledInput-root': {
//                                                                 border:
//                                                                     errors['costCenterValue'] && touched['costCenterValue']
//                                                                         ? '1px solid red'
//                                                                         : '1px solid #00000000',
//                                                                 '&.Mui-focused': { backgroundColor: 'white', border: '1px solid black' },
//                                                             },
//                                                         }}
//                                                         onChange={handleChange}
//                                                         onBlur={handleBlur}
//                                                         InputProps={{ disableUnderline: true, style: { borderRadius: '8px' } }}
//                                                     />
//                                                     <div style={{ color: 'red', marginTop: '8px', marginLeft: '4px' }}>
//                                                         <ErrorMessage name="costCenterValue" component="div" />
//                                                     </div>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={1}></Grid> {/* Spacer */}
//                                             </Grid>

//                                             {/* Dynamic Key Value Pairs */}
//                                             {values.costLabelPairs?.map((pair, index) => (
//                                                 // Use React.Fragment for key prop
//                                                 <React.Fragment key={pair.id || index}>
//                                                     <Grid container spacing={4} mt="8px" alignItems="center">
//                                                         <Grid item xs={12} sm={6}>
//                                                             <TextField
//                                                                 fullWidth
//                                                                 placeholder="Enter key"
//                                                                 size="medium"
//                                                                 hiddenLabel
//                                                                 // Bind value directly to the specific item in the array
//                                                                 value={values.costLabelPairs[index].cost}
//                                                                 name={`costLabelPairs[${index}].cost`} // Name for Formik error handling
//                                                                 sx={{
//                                                                     '& .MuiFilledInput-root': {
//                                                                          border:
//                                                                             getIn(errors, `costLabelPairs[${index}].cost`) && getIn(touched, `costLabelPairs[${index}].cost`)
//                                                                                 ? '1px solid red'
//                                                                                 : '1px solid #00000000',
//                                                                         '&.Mui-focused': { backgroundColor: 'white', border: '1px solid black' },
//                                                                     },
//                                                                 }}
//                                                                 variant="filled"
//                                                                 onChange={handleChange} // Let Formik handle change
//                                                                 onBlur={handleBlur}
//                                                                 InputProps={{ disableUnderline: true, style: { borderRadius: '8px' } }}
//                                                             />
//                                                             <div style={{ color: 'red', marginTop: '8px', marginLeft: '4px' }}>
//                                                                 <ErrorMessage name={`costLabelPairs[${index}].cost`} component="div" />
//                                                             </div>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={5}> {/* Adjusted size */}
//                                                             <TextField
//                                                                 fullWidth
//                                                                 placeholder="Enter value"
//                                                                 size="medium"
//                                                                 sx={{
//                                                                      '& .MuiFilledInput-root': {
//                                                                          border:
//                                                                             getIn(errors, `costLabelPairs[${index}].label`) && getIn(touched, `costLabelPairs[${index}].label`)
//                                                                                 ? '1px solid red'
//                                                                                 : '1px solid #00000000',
//                                                                         '&.Mui-focused': { backgroundColor: 'white', border: '1px solid black' },
//                                                                     },
//                                                                 }}
//                                                                 // Bind value directly
//                                                                 value={values.costLabelPairs[index].label}
//                                                                 name={`costLabelPairs[${index}].label`} // Name for Formik error handling
//                                                                 hiddenLabel
//                                                                 variant="filled"
//                                                                 onChange={handleChange} // Let Formik handle change
//                                                                 onBlur={handleBlur}
//                                                                 InputProps={{ disableUnderline: true, style: { borderRadius: '8px' } }}
//                                                             />
//                                                             <div style={{ color: 'red', marginTop: '8px', marginLeft: '4px' }}>
//                                                                 <ErrorMessage name={`costLabelPairs[${index}].label`} component="div" />
//                                                             </div>
//                                                         </Grid>
//                                                          <Grid item xs={12} sm={1} sx={{ textAlign: 'center' }}> {/* Delete button column */}
//                                                             <Button
//                                                                 color="error" // Make it look like a delete button
//                                                                 onClick={() => {
//                                                                     const filteredPairs = values.costLabelPairs.filter((_, i) => i !== index);
//                                                                     setFieldValue('costLabelPairs', filteredPairs);
//                                                                 }}
//                                                                 sx={{ minWidth: 'auto', padding: '6px' }} // Adjust button styling
//                                                             >
//                                                                 <img alt="close-icon" src="/assets/close-icon.svg" style={{width: '18px', height: '18px'}}/> {/* Adjust icon size if needed */}
//                                                             </Button>
//                                                         </Grid>
//                                                     </Grid>
//                                                 </React.Fragment>
//                                             ))}
//                                             <Button
//                                                 variant="text"
//                                                 sx={{ color: '#000000', textTransform: 'none', mt: '16px', fontWeight: 'bold' }}
//                                                 onClick={() =>
//                                                     setFieldValue('costLabelPairs', [
//                                                         ...(values.costLabelPairs || []), // Handle potential null/undefined
//                                                         { cost: '', label: '', id: crypto.randomUUID() }, // Use crypto.randomUUID if available
//                                                     ])
//                                                 }
//                                             >
//                                                 + Add Key Value Pair
//                                             </Button>
//                                         </div>

//                                         {/* --- Action Buttons --- */}
//                                         <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
//                                             <Button
//                                                 sx={{
//                                                     color: 'black',
//                                                     borderColor: 'black',
//                                                     fontWeight: 'bold',
//                                                     textTransform: 'none',
//                                                     px: 4,
//                                                 }}
//                                                 variant="outlined"
//                                                 size="large"
//                                                 // Navigate back appropriately
//                                                 onClick={() => navigate(isEdit ? `/applications/${id}` : '/applications')}
//                                             >
//                                                 Cancel
//                                             </Button>
//                                             <Button
//                                                 sx={{
//                                                     backgroundColor: "black",
//                                                     color: "white",
//                                                     fontWeight: "bold",
//                                                     "&:hover": {
//                                                         backgroundColor: "rgba(0,0,0,0.8)", // Slightly lighter black on hover
//                                                         // color: "black", // Keep text white or change as needed
//                                                         // borderColor: "black",
//                                                     },
//                                                      "&:disabled": { // Style for disabled state
//                                                         backgroundColor: "grey",
//                                                         color: "lightgrey"
//                                                     },
//                                                     textTransform: "none",
//                                                     px: 4,
//                                                 }}
//                                                 variant="contained"
//                                                 size="large"
//                                                 // REMOVED type="submit"
//                                                 onClick={() => setOpenModal(true)} // Open the modal
//                                                 disabled={!(isValid && dirty)} // Keep disable logic
//                                             >
//                                                 {/* Changed Button Text */}
//                                                 {isEdit ? "Review and Save" : "Review and Add"}
//                                             </Button>
//                                         </Box>
//                                     </Box>

//                                     {/* --- Confirmation Modal --- */}
//                                     <Modal
//                                         open={openModal}
//                                         onClose={() => setOpenModal(false)} // Allow closing by clicking backdrop/Escape key
//                                         aria-labelledby="review-modal-title"
//                                         aria-describedby="review-modal-description"
//                                     >
//                                         <Box sx={modalStyle}>
//                                             <Typography id="review-modal-title" variant="h6" component="h2" gutterBottom>
//                                                 {isEdit ? "Confirm Changes" : "Confirm New Application"}
//                                             </Typography>
//                                             <Typography id="review-modal-description" sx={{ mt: 2 }}>
//                                                 Please review the details before submitting:
//                                                 <Box component="ul" sx={{ pl: 2, mt: 1, maxHeight: '200px', overflowY: 'auto' }}> {/* Added scroll */}
//                                                     <li><strong>Name:</strong> {values.applicationName}</li>
//                                                     <li><strong>Cluster:</strong> {values.cluster}</li>
//                                                     <li><strong>Environment:</strong> {values.environment}</li>
//                                                     <li><strong>Viewers:</strong> {values.gcpApplicationViewers?.join(', ') || 'None'}</li>
//                                                     <li><strong>Owners:</strong> {values.serviceRequesters?.join(', ') || 'None'}</li>
//                                                     <li><strong>Cost Center:</strong> {values.costCenterValue}</li>
//                                                      {values.costLabelPairs?.map((p, i) => (
//                                                         <li key={i}><strong>{p.cost || '(empty key)'}:</strong> {p.label || '(empty value)'}</li>
//                                                     ))}
//                                                 </Box>
//                                             </Typography>
//                                             <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
//                                                 <Button variant="outlined" onClick={() => setOpenModal(false)}>
//                                                     Cancel
//                                                 </Button>
//                                                 <Button
//                                                     variant="contained"
//                                                     onClick={() => {
//                                                         handleSubmit(); // <-- Trigger Formik's submit function
//                                                         setOpenModal(false); // Close modal after clicking confirm
//                                                     }}
//                                                     sx={{ backgroundColor: 'black', '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' } }}
//                                                 >
//                                                     {isEdit ? "Save Changes" : "Confirm & Add Application"}
//                                                 </Button>
//                                             </Box>
//                                         </Box>
//                                     </Modal>
//                                 </Form>
//                             );
//                         }}
//                     </Formik>
//                 )}
//             </Grid>
//             <Grid item xs={1}></Grid>
//         </Grid>
// 		</Container>
// 	);
// };

// export default ApplicationRequesterForm;


